import { animate, style, transition, trigger } from "@angular/animations";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  ViewChild,
  ViewChildren
} from "@angular/core";
import { IonRouterOutlet, ViewWillEnter } from "@ionic/angular";
import { MessagesService } from "./../../_services/messages.service";
import { Message } from "./../../helpers/types";

import { ActivatedRoute, Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core"; // if using ngx-translate
import { map, Subscription } from "rxjs";
import { AppService } from "src/app/_services/app.service";
import { IosGestureService } from "src/app/_services/ios-gesture.service";
import { KeyboardService } from "src/app/_services/keyboard.service";
import { NavigationService } from "src/app/_services/navigation-service.service";
import { LocalStorageService } from "../../_services/localStorage.service";
import { AudioPlayer } from "../audio-player/audio-player.component";
import { MessagerieTextInputComponent } from "../messagerie-text-input/messagerie-text-input.component";

@Component({
  selector: "app-messagerier",
  templateUrl: "./messagerier.component.html",
  styleUrls: ["./messagerier.component.scss"],
  standalone: false,
  animations: [
    trigger("slideInTop", [
      transition(":enter", [
        style({ transform: "translateY(100%)", opacity: 0 }),
        animate(
          "500ms ease-out",
          style({ transform: "translateX(0%)", opacity: 1 })
        ),
      ]),
      transition(":leave", [
        style({ transform: "translateX(0%)", opacity: 1 }),
        animate(
          "5500ms ease-in",
          style({ transform: "translateX(-100%)", opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export class MessagerierComponent implements OnInit, OnDestroy, ViewWillEnter {
  current_user: any;
  receiver_id!: string;
  receiver_data: any;
  message: string = "";
  messagesType: string = "users";
  convMessages: any[] = [];
  progressBarValue: number = 0.0;
  private audio!: HTMLMediaElement;
  conversatinName: string = "";
  guest: boolean = false;
  isLoading: boolean = true;
  isKeyboardVisible: boolean = false;
  private keyboardHeight: number = 0;
  private keyboardSubscription?: Subscription;
  private keyboardWillShowListener?: any;
  private keyboardWillHideListener?: any;
  private viewportResizeHandler?: () => void;
  private windowResizeHandler?: () => void;
  private scrollCheckInterval?: number;
  @ViewChild("audioElement", { static: false }) public _audioRef!: ElementRef;
  @ViewChild("messageInput") messageInputRef!: ElementRef;
  @ViewChild("messageContainer") messageContainer!: ElementRef;
  @ViewChildren(AudioPlayer) audioPlayers!: QueryList<AudioPlayer>;
  @ViewChild(MessagerieTextInputComponent) textInputComponent?: MessagerieTextInputComponent;

  constructor(
    private messagesService: MessagesService,
    private appService: AppService,
    private localStorageService: LocalStorageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private navigationService: NavigationService,
    private iosGestureService: IosGestureService,
    private keyboardService: KeyboardService,
    private platform: Platform,
    @Optional() private ionRouterOutlet?: IonRouterOutlet
  ) { }

  ionViewWillEnter(): void {
    this.appService.refreshHomeDataStats();
    this.getConversationData();
  }
  ngOnDestroy(): void {
    // Re-enable iOS swipe-back gesture when leaving the page
    this.iosGestureService.enableSwipeBack();
    this.iosGestureService.unregisterOutlet();

    // Unsubscribe from keyboard events
    this.keyboardSubscription?.unsubscribe();

    // Clean up Capacitor keyboard listeners
    if (this.keyboardWillShowListener) {
      this.keyboardWillShowListener.remove();
    }
    if (this.keyboardWillHideListener) {
      this.keyboardWillHideListener.remove();
    }

    // Clean up viewport listeners
    if (this.viewportResizeHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
    }
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
    }
    if (this.scrollCheckInterval) {
      clearInterval(this.scrollCheckInterval);
    }

    const userDate = this.localStorageService.getToken();
    if (userDate) {
      // Refresh unread/conversation cache when leaving chat.
      this.messagesService.initUnreadConversation();
    }
  }
  ngAfterViewInit() {
    this.audio = this._audioRef?.nativeElement;
    if (this.audio) {
      this.audio.autoplay = false;
      this.audio.volume = 1;
    }
    this.scrollToBottom();
    this.setupViewportScrollHandlers();
  }

  private setupViewportScrollHandlers(): void {
    // Visual Viewport API listener is set up in setupKeyboardHeightListeners() for iOS
    // This method handles additional scroll/viewport adjustments

    // Also listen to window resize as fallback
    this.windowResizeHandler = () => {
      if (this.isKeyboardVisible) {
        this.updateKeyboardHeightFromViewport();
        this.adjustPaddingForKeyboard(true);
        this.ensureInputVisible();
      }
    };
    window.addEventListener('resize', this.windowResizeHandler);
  }

  private ensureInputVisible(): void {
    const inputElement = document.querySelector('.message-input-wrapper') as HTMLElement;
    if (!inputElement) return;

    // Use Visual Viewport API if available
    if (window.visualViewport) {
      const viewport = window.visualViewport;
      const inputRect = inputElement.getBoundingClientRect();
      const viewportHeight = viewport.height;
      const viewportTop = viewport.offsetTop;
      const inputBottom = inputRect.bottom - viewportTop;

      // If input is below visible viewport, scroll to show it
      if (inputBottom > viewportHeight - 100) {
        const scrollAmount = inputBottom - viewportHeight + 150;
        // Try multiple scroll methods for iOS Safari
        window.scrollBy(0, scrollAmount);
        document.documentElement.scrollTop += scrollAmount;
        document.body.scrollTop += scrollAmount;
      }
    } else {
      // Fallback: use window.innerHeight
      const inputRect = inputElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const inputBottom = inputRect.bottom;

      if (inputBottom > viewportHeight - 100) {
        const scrollAmount = inputBottom - viewportHeight + 150;
        // Try multiple scroll methods
        window.scrollBy(0, scrollAmount);
        document.documentElement.scrollTop += scrollAmount;
        document.body.scrollTop += scrollAmount;

        // Also try scrollIntoView as backup
        inputElement.scrollIntoView({
          behavior: 'auto',
          block: 'end',
          inline: 'nearest'
        });
      }
    }

    // Force a reflow to ensure scroll happens
    void inputElement.offsetHeight;
  }

  ngOnInit() {
    // Disable iOS swipe-back gesture for this page
    this.iosGestureService.registerOutlet(this.ionRouterOutlet);
    this.iosGestureService.disableSwipeBack();

    // Setup keyboard height listeners for iOS
    if (this.platform.is('ios')) {
      this.setupKeyboardHeightListeners();
    }

    // Subscribe to keyboard state changes
    this.keyboardSubscription = this.keyboardService.getKeyboardState().subscribe(
      (isVisible: boolean) => {
        this.isKeyboardVisible = isVisible;
        this.adjustPaddingForKeyboard(isVisible);

        // When keyboard opens, ensure input is visible
        if (isVisible) {
          // Try multiple times as keyboard animates
          setTimeout(() => this.ensureInputVisible(), 100);
          setTimeout(() => this.ensureInputVisible(), 200);
          setTimeout(() => this.ensureInputVisible(), 300);
          setTimeout(() => this.ensureInputVisible(), 500);

          // Start continuous checking for iOS web
          if (this.platform.is('ios') && !Capacitor.isNativePlatform()) {
            if (this.scrollCheckInterval) {
              clearInterval(this.scrollCheckInterval);
            }
            let checkCount = 0;
            this.scrollCheckInterval = window.setInterval(() => {
              this.ensureInputVisible();
              this.updateKeyboardHeightFromViewport();
              checkCount++;
              if (checkCount >= 20) { // Stop after 2 seconds
                clearInterval(this.scrollCheckInterval);
                this.scrollCheckInterval = undefined;
              }
            }, 100);
          }
        } else {
          // Stop continuous checking when keyboard closes
          if (this.scrollCheckInterval) {
            clearInterval(this.scrollCheckInterval);
            this.scrollCheckInterval = undefined;
          }
          this.keyboardHeight = 0;
        }
      }
    );

    this.getUserData();
    this.receiver_id = this.route.snapshot.paramMap.get("id")!;
    this.messagesType = this.receiver_id === "admin" ? "admins" : "users";
    this.realTimeMessage();

    if (this.messagesType == "users") {
      this.isLoading = true;
      this.appService.getUserById(this.receiver_id).subscribe((res: any) => {
        this.receiver_data = res.body;
      });
      this.getMessages();
    } else if (this.messagesType === "admins") {
      this.isLoading = true;
      this.getAdminMessages();
    }

    // React to future route changes within the same component instance
    this.route.data.subscribe((data) => {
      this.conversatinName = data['conversatinName'] ?? this.conversatinName;
      this.guest = data['guest'] ?? this.guest;
    });
    this.route.paramMap.subscribe((params) => {
      const newId = params.get('id');
      if (newId && newId !== this.receiver_id) {
        this.receiver_id = newId;
        this.messagesType = this.receiver_id === "admin" ? "admins" : "users";

        if (this.messagesType === "users") {
          this.isLoading = true;
          this.appService.getUserById(this.receiver_id).subscribe((res: any) => {
            this.receiver_data = res.body;
          });
          this.getMessages();
        } else {
          this.isLoading = true;
          this.getAdminMessages();
        }
      }
    });
  }

  getConversationData() {
    // 1) From static route data (fallbacks)
    this.conversatinName = this.route.snapshot.data['conversatinName'] ?? this.conversatinName;
    this.guest = this.route.snapshot.data['guest'] ?? this.guest;

    // 2) From NavigationExtras.state (preferred when navigating with dynamic data)
    const navState: any = (this.router.getCurrentNavigation()?.extras.state as any) ?? history.state;
    if (navState) {
      if (navState.conversatinName) {
        this.conversatinName = navState.conversatinName;
      }
      if (typeof navState.guest === 'boolean') {
        this.guest = navState.guest;
      }
    }
  }
  getConversationName() {
    const receiverName = this.receiver_data ? `${this.receiver_data.first_name} ${this.receiver_data.last_name}` : "";
    const result = receiverName.length > 0 ? receiverName : this.conversatinName;
    return result;
  }

  realTimeMessage() {
    this.messagesService.getRealtimeMessages().subscribe((message: any) => {
      if (this.messagesType === "admins") {
        let newMessage = message.response;
        const messageConetent = newMessage.message;
        const newMessageOject: Message = {
          sender: 1,
          last_message: messageConetent,
          created_at: Date(),
          updated_at: Date(),
          is_read: 0,
          id: 0,
          type: newMessage.type ? newMessage.type : "text",
          duration: newMessage.duration ? newMessage.duration : null,
        };
        this.addMessageToGroup(newMessageOject, this.convMessages.length - 1);
      } else if (this.messagesType == "users") {
        const senderId = message.sender.id;

        const messageConetent = message.message;
        if (senderId == this.receiver_id) {
          const newMessage: Message = {
            user1_id: Number(this.receiver_id),
            user2_id: this.current_user.id,
            message: messageConetent,
            created_at: Date(),
            updated_at: Date(),
            is_read: 0,
            id: 0,
            type: message.type ? message.type : "text",
            duration: message.duration ? message.duration : null,
          };

          this.addMessageToGroup(newMessage, this.convMessages.length - 1);
        }
      }
    });
  }

  getUserData() {
    const { client, token } = this.localStorageService.getUserData();
    this.current_user = client;
  }

  goBack() {
    // Use NavigationService for smart back navigation
    // this.navigationService.smartBack('/particulier');
    this.navigationService.goToPreviousPage();

  }
  getMessages() {
    this.messagesService.getMessage(this.receiver_id).subscribe((res: any) => {
      this.messagesService.readUserConversations(Number(this.receiver_id));
      const groupedMessages = this.groupMessagesByDate(res.discussions);
      this.convMessages = groupedMessages;
      this.scrollToBottom();
      this.isLoading = false;
    });
  }
  sendMessage(data: any) {
    this.messagesService
      .sendMessage(this.receiver_id, data)
      .subscribe((res: any) => {
        const newMessage: Message = {
          user1_id: this.current_user.id,
          user2_id: Number(this.receiver_id),
          message: res.discussion.message,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_read: 0,
          id: 0,
          type: res.discussion.type,
          duration: res.discussion.duration,
        };
        this.addMessageToGroup(newMessage, this.convMessages.length - 1);
        this.messagesService.notifyConversationListUpdated({
          peerId: Number(this.receiver_id),
          message: res?.discussion?.message ?? data?.message ?? "",
          updated_at: new Date().toISOString(),
          is_read: 1,
        });
      });
  }

  getAdminMessages() {
    this.messagesService
      .getAdminMessages()
      .pipe(
        map((res: any) => {
          // Map through the array and assign 'text' if type is missing
          return res.map((message: any) => {
            if (!message.type) {
              message.type = "text";
            }
            return message;
          });
        })
      )
      .subscribe((res: any[]) => {
        const groupedMessages = this.groupMessagesByDate(res);
        this.appService.refreshHomeDataStats();
        this.convMessages = groupedMessages;
        this.scrollToBottom();
        this.isLoading = false;
      });

  }

  handleMessage(data: any) {
    const payload = data.message.length
      ? {
        type: data.voiceMessage ? "audio" : "text",
        duration: data.duration ? data.duration : 0,
        message: data.message,
        voiceMessage: data.voiceMessage,
        file: data.voiceMessage,
        extension: data.extension,
      }
      : {
        type: data.voiceMessage ? "audio" : "text",
        duration: data.duration ? data.duration : 0,
        voiceMessage: data.voiceMessage,
        file: data.voiceMessage,
        extension: data.extension,
      };
    if (data && (data.message.trim() || data.voiceMessage)) {
      if (this.messagesType === "admins") {
        this.sendAdminMessage(payload);
      } else if (this.messagesType === "users") {
        this.sendMessage(payload);
      }
      setTimeout(() => {
        this.message = ""; // Clear the input after sending
      }, 500);
    }
  }
  sendAdminMessage(payload: any) {
    this.messagesService.sendAdminMessage(payload).subscribe((res: any) => {
      const data = res.data;
      const newMessage: Message = {
        sender: 0,
        last_message: data.message,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_read: 0,
        id: 0,
        type: data.type,
        duration: data.duration ? data.duration : null,
      };
      this.addMessageToGroup(newMessage, this.convMessages.length - 1);
      this.messagesService.notifyConversationListUpdated({
        peerId: "admin",
        message: data?.message ?? "",
        updated_at: new Date().toISOString(),
        is_read: 1,
      });
    });
  }

  onMessageInputChange($value: any) {
    this.message = $value;
  }

  addMessageToGroup(
    newMessage: any,
    groupIndex: number,
    scroll: boolean = true
  ) {
    if (this.convMessages.length) {
      this.convMessages[groupIndex].messages.push(newMessage);
    } else {
      this.convMessages[0] = {
        date: "aujourd'hui",
        messages: [newMessage],
      };
    }
    scroll && this.scrollToBottom();
  }
  scrollToBottom(): void {
    try {
      setTimeout(() => {
        const height = this.messageContainer.nativeElement.scrollHeight;
        this.messageContainer.nativeElement.scrollTop = height;
      }, 10);
    } catch (err) { }
  }

  onInputFocus(): void {
    // When input is focused, ensure it's visible above keyboard
    // Try multiple times as keyboard animates
    setTimeout(() => this.ensureInputVisible(), 100);
    setTimeout(() => this.ensureInputVisible(), 200);
    setTimeout(() => this.ensureInputVisible(), 300);
    setTimeout(() => this.ensureInputVisible(), 500);
    setTimeout(() => this.ensureInputVisible(), 700);

    // Start continuous checking for iOS web
    if (this.platform.is('ios') && !Capacitor.isNativePlatform()) {
      // Clear any existing interval
      if (this.scrollCheckInterval) {
        clearInterval(this.scrollCheckInterval);
      }

      // Check every 100ms for 2 seconds to catch keyboard animation
      let checkCount = 0;
      this.scrollCheckInterval = window.setInterval(() => {
        this.ensureInputVisible();
        checkCount++;
        if (checkCount >= 20) { // Stop after 2 seconds
          clearInterval(this.scrollCheckInterval);
          this.scrollCheckInterval = undefined;
        }
      }, 100);
    }

    // Also scroll messages to bottom
    setTimeout(() => {
      this.scrollToBottom();
    }, 300);
  }

  handleAudioPlay(player: AudioPlayer): void {
    this.stopRecordingIfActive();
    this.stopAllAudioPlayers(player);
  }

  onRecordingStateChange(isRecording: boolean): void {
    if (isRecording) {
      this.stopAllAudioPlayers();
    }
  }

  private stopAllAudioPlayers(exceptPlayer?: AudioPlayer): void {
    if (!this.audioPlayers) {
      return;
    }
    this.audioPlayers.forEach((audioInstance) => {
      if (!exceptPlayer || audioInstance !== exceptPlayer) {
        audioInstance.stopPlayback();
      }
    });
  }

  private stopRecordingIfActive(): void {
    this.textInputComponent?.forceStopRecording();
  }

  private setupKeyboardHeightListeners(): void {
    // For native iOS, use Capacitor Keyboard API
    if (Capacitor.isNativePlatform() && this.platform.is('ios')) {
      this.keyboardWillShowListener = Keyboard.addListener('keyboardWillShow', (info) => {
        this.keyboardHeight = info.keyboardHeight;
        if (this.isKeyboardVisible) {
          this.adjustPaddingForKeyboard(true);
        }
      });

      this.keyboardWillHideListener = Keyboard.addListener('keyboardWillHide', () => {
        this.keyboardHeight = 0;
        if (!this.isKeyboardVisible) {
          this.adjustPaddingForKeyboard(false);
        }
      });
    } else if (window.visualViewport && this.platform.is('ios') && !Capacitor.isNativePlatform()) {
      // For iOS web, use Visual Viewport API
      let initialViewportHeight = window.visualViewport.height;

      this.viewportResizeHandler = () => {
        const viewport = window.visualViewport!;
        const viewportHeight = viewport.height;
        const keyboardHeight = initialViewportHeight - viewportHeight;

        if (keyboardHeight > 50) {
          this.keyboardHeight = keyboardHeight;
        } else {
          this.keyboardHeight = 0;
          initialViewportHeight = viewportHeight;
        }

        // Update padding and ensure input is visible
        if (this.isKeyboardVisible) {
          this.adjustPaddingForKeyboard(true);
        }
        this.ensureInputVisible();
      };

      window.visualViewport.addEventListener('resize', this.viewportResizeHandler);
      window.visualViewport.addEventListener('scroll', this.viewportResizeHandler);
    }
  }

  private updateKeyboardHeightFromViewport(): void {
    if (window.visualViewport && this.platform.is('ios') && !Capacitor.isNativePlatform()) {
      const viewport = window.visualViewport;
      const viewportHeight = viewport.height;
      const windowHeight = window.innerHeight;
      const keyboardHeight = Math.max(windowHeight - viewportHeight, 0);

      if (keyboardHeight > 50) {
        this.keyboardHeight = keyboardHeight;
      }
    }
  }

  private async adjustPaddingForKeyboard(isKeyboardVisible: boolean): Promise<void> {
    if (!this.messageContainer) {
      return;
    }

    try {
      const wrapper = this.messageContainer.nativeElement;
      const basePadding = 73 + 40; // Input height + spacing
      const safeArea = this.getSafeAreaBottom();

      if (isKeyboardVisible && this.keyboardHeight > 0) {
        // Use actual keyboard height
        const totalPadding = basePadding + this.keyboardHeight + safeArea + 20; // Extra 20px buffer
        wrapper.style.setProperty('padding-bottom', `${totalPadding}px`, 'important');

        // Scroll viewport to show input field when keyboard opens
        setTimeout(() => {
          this.ensureInputVisible();
          this.scrollToBottom();
        }, 200);
      } else {
        // When keyboard is closed, use normal padding
        const totalPadding = basePadding + safeArea;
        wrapper.style.setProperty('padding-bottom', `${totalPadding}px`, 'important');

        // Scroll to bottom after keyboard closes
        setTimeout(() => {
          this.scrollToBottom();
        }, 150);
      }
    } catch (err) {
      console.error('Error adjusting padding for keyboard:', err);
    }
  }

  private getSafeAreaBottom(): number {
    // Get safe area inset bottom for devices with home indicator
    try {
      const safeArea = getComputedStyle(document.documentElement)
        .getPropertyValue('env(safe-area-inset-bottom)') ||
        getComputedStyle(document.documentElement)
          .getPropertyValue('constant(safe-area-inset-bottom)') || '0px';
      return parseInt(safeArea.replace('px', '')) || 0;
    } catch (e) {
      return 0;
    }
  }

  groupMessagesByDate(messages: any[]): any[] {
    const groupedMessages: any[] = [];
    const messagesList = messages;
    messagesList.sort((a: any, b: any) => {
      return a.message_id - b.message_id; // Assuming message_id is a numerical property
    });
    messagesList.forEach((message) => {
      const createdAt = new Date(message.created_at);
      const dateString = this.getDateString(createdAt);
      const existingGroupIndex = groupedMessages.findIndex(
        (group) => group.date === dateString
      );
      if (existingGroupIndex !== -1) {
        groupedMessages[existingGroupIndex].messages.push(message);
      } else {
        groupedMessages.push({ date: dateString, messages: [message] });
      }
    });

    return groupedMessages;
  }

  getDateString(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (this.isSameDay(date, today)) {
      return this.translate.instant("SHORT_MONTH_NAME.TODAY");
    } else if (this.isSameDay(date, yesterday)) {
      return this.translate.instant("SHORT_MONTH_NAME.YESTERDAY");
    } else {
      const months = [
        "SHORT_MONTH_NAME.Jan",
        "SHORT_MONTH_NAME.Feb",
        "SHORT_MONTH_NAME.Mar",
        "SHORT_MONTH_NAME.Apr",
        "SHORT_MONTH_NAME.May",
        "SHORT_MONTH_NAME.Jun",
        "SHORT_MONTH_NAME.Jul",
        "SHORT_MONTH_NAME.Aug",
        "SHORT_MONTH_NAME.Sep",
        "SHORT_MONTH_NAME.Oct",
        "SHORT_MONTH_NAME.Nov",
        "SHORT_MONTH_NAME.Dec",
      ];
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${this.translate.instant(month)} ${year}`;
    }
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  toNumber(str: string): number {
    return parseInt(str, 10);
  }
  fetchTime(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  }
  fetchTimeWithoutSeconds(timestamp: string): string {
    const date = new Date(timestamp);
    const hours = this.padZero(date.getHours());
    const minutes = this.padZero(date.getMinutes());
    return `${hours}:${minutes}`;
  }

  private padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  fetchDate(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toDateString();
  }
  onTimeUpdate(event: any) {
    const audioElement: HTMLAudioElement = event.target;
    this.progressBarValue = audioElement.currentTime;
  }
  // get timeValue() {
  //   return this.currentDemande?.duration / 1000 - this.progressBarValue;
  // }
}
